import { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Minus, Trash2, ShoppingCart, Edit2, ChevronDown, ChevronUp, Pencil } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderPage = () => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [extras, setExtras] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [cart, setCart] = useState([]);
  const [activeOrders, setActiveOrders] = useState([]);
  const [orderType, setOrderType] = useState("paket");
  const [paymentMethod, setPaymentMethod] = useState("nakit");
  const [mealCardType, setMealCardType] = useState("sodexo");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [lastCreatedOrder, setLastCreatedOrder] = useState(null);
  const [showFinalPaymentModal, setShowFinalPaymentModal] = useState(false);
  const [selectedOrderForPayment, setSelectedOrderForPayment] = useState(null);
  

  // Düzenleme (bekleyen sipariş)
  const [isEditingOrder, setIsEditingOrder] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);

  // Ingredient customization modal states
  const [showIngredientModal, setShowIngredientModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editingCartIndex, setEditingCartIndex] = useState(null);
  const [removedIngredients, setRemovedIngredients] = useState([]);
  const [addedIngredients, setAddedIngredients] = useState([]);
  const [customQuantity, setCustomQuantity] = useState(1);
  const [specialNote, setSpecialNote] = useState("");
  const [selectedSize, setSelectedSize] = useState(null);
  const [isExtrasOpen, setIsExtrasOpen] = useState(false); // Ekstra malzeme accordion
  const [isIngredientsOpen, setIsIngredientsOpen] = useState(false); // Çıkarılacak malzeme accordion

  useEffect(() => {
    loadCategories();
    loadProducts();
    loadExtras();
    loadActiveOrders();
    
    const interval = setInterval(loadActiveOrders, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
      // İlk kategoriyi otomatik seç
      if (response.data.length > 0 && !selectedCategory) {
        setSelectedCategory(response.data[0].id);
      }
    } catch (error) {
      console.error("Kategoriler yüklenirken hata:", error);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error("Ürünler yüklenirken hata:", error);
    }
  };

  const loadExtras = async () => {
    try {
      const response = await axios.get(`${API}/extras`);
      setExtras(response.data);
    } catch (error) {
      console.error("Ekstra malzemeler yüklenirken hata:", error);
    }
  };

  const loadActiveOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      // Sipariş sekmesi: Sadece order kaynaklı paket/gel-al ve ödenmemiş siparişler
      const quickOrders = response.data.filter(
        order => (order.order_type === "paket" || order.order_type === "gel-al") && 
                 order.payment_status === "ödenmedi" &&
                 (order.order_source === "order" || (!order.order_source && !order.customer_phone))
      );
      setActiveOrders(quickOrders);
    } catch (error) {
      console.error("Siparişler yüklenirken hata:", error);
    }
  };

  const filteredProducts = selectedCategory
    ? products.filter(p => p.category_id === selectedCategory)
    : products;

  // Ingredient customization functions
  const openIngredientModal = (product, cartIndex = null) => {
    setSelectedProduct(product);
    setEditingCartIndex(cartIndex);
    setIsExtrasOpen(false); // Accordion kapalı başlasın
    setIsIngredientsOpen(false); // Çıkarılacak malzeme accordion kapalı
    
    if (cartIndex !== null) {
      const item = cart[cartIndex];
      setRemovedIngredients(item.removed_ingredients || []);
      setAddedIngredients(item.added_ingredients || []);
      setCustomQuantity(item.quantity);
      setSpecialNote(item.special_note || "");
      setSelectedSize(item.size || (product.sizes && product.sizes.length > 0 ? product.sizes[0].name : null));
    } else {
      setRemovedIngredients([]);
      setAddedIngredients([]);
      setCustomQuantity(1);
      setSpecialNote("");
      setSelectedSize(product.sizes && product.sizes.length > 0 ? product.sizes[0].name : null);
    }
    
    setShowIngredientModal(true);
  };

  const toggleRemovedIngredient = (ingredient) => {
    if (removedIngredients.includes(ingredient)) {
      setRemovedIngredients(removedIngredients.filter(i => i !== ingredient));
    } else {
      setRemovedIngredients([...removedIngredients, ingredient]);
    }
  };

  const toggleAddedIngredient = (extraId) => {
    if (addedIngredients.includes(extraId)) {
      setAddedIngredients(addedIngredients.filter(i => i !== extraId));
    } else {
      setAddedIngredients([...addedIngredients, extraId]);
    }
  };

  const addCustomizedToCart = () => {
    let finalPrice = selectedProduct.price;
    
    if (selectedProduct.sizes && selectedSize) {
      const sizeObj = selectedProduct.sizes.find(s => s.name === selectedSize);
      if (sizeObj) {
        finalPrice = sizeObj.price;
      }
    }
    
    addedIngredients.forEach(extraId => {
      const extra = extras.find(e => e.id === extraId);
      if (extra) {
        if (extra.size_prices && selectedSize) {
          const sizePrice = extra.size_prices.find(sp => sp.size === selectedSize.toLowerCase());
          finalPrice += sizePrice ? sizePrice.price : extra.price;
        } else {
          finalPrice += extra.price;
        }
      }
    });
    
    // Prepare added ingredients with prices
    const addedIngredientsDetails = addedIngredients.map(id => {
      const extra = extras.find(e => e.id === id);
      if (!extra) return null;
      
      let price = extra.price;
      if (extra.size_prices && selectedSize) {
        const sizePrice = extra.size_prices.find(sp => sp.size === selectedSize.toLowerCase());
        price = sizePrice ? sizePrice.price : extra.price;
      }
      
      return {
        name: extra.name,
        price: price
      };
    }).filter(Boolean);
    
    const newItem = {
      product_id: selectedProduct.id,
      product_name: selectedProduct.name,
      price: finalPrice,
      quantity: customQuantity,
      size: selectedSize,
      removed_ingredients: removedIngredients,
      added_ingredients: addedIngredients.map(id => extras.find(e => e.id === id)?.name).filter(Boolean),
      added_ingredients_details: addedIngredientsDetails, // NEW: with prices
      special_note: specialNote
    };
    
    if (editingCartIndex !== null) {
      const updatedCart = [...cart];
      updatedCart[editingCartIndex] = newItem;
      setCart(updatedCart);
    } else {
      setCart([...cart, newItem]);
    }
    
    setShowIngredientModal(false);
  };

  const addToCart = (product) => {
    if ((product.ingredients && product.ingredients.length > 0) || 
        (product.extra_ids && product.extra_ids.length > 0) ||
        (product.sizes && product.sizes.length > 0)) {
      openIngredientModal(product);
    } else {
      setCart([...cart, {
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        price: product.price,
        removed_ingredients: [],
        added_ingredients: [],
        special_note: null
      }]);
    }
  };

  const updateQuantity = (index, delta) => {
    setCart(cart.map((item, idx) => {
      if (idx === index) {
        const newQty = item.quantity + delta;
        if (newQty <= 0) {
          return null;
        }
        return { ...item, quantity: newQty };
      }
      return item;
    }).filter(Boolean));
  };

  const removeFromCart = (productId, index = null) => {
    if (index !== null) {
      setCart(cart.filter((_, idx) => idx !== index));
    } else {
      setCart(cart.filter(item => item.product_id !== productId));
    }
  };

  const calculateTotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0).toFixed(2);
  };

  const getElapsedTime = (dateString) => {
    const orderDate = new Date(dateString);
    const now = new Date();
    const diffMs = now - orderDate;
    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    
    if (hours > 0) {
      return `${hours}s ${mins}dk`;
    } else {
      return `${mins}dk`;
    }
  };

  const createOrder = async (type) => {
    if (cart.length === 0) {
      alert("Sepet boş, lütfen ürün ekleyin");
      return;
    }
    setOrderType(type);
    setShowPaymentModal(true);
  };

  const completeOrder = async () => {
    try {
      const finalPaymentMethod = paymentMethod === "yemek-karti" 
        ? `Yemek Kartı (${mealCardType.charAt(0).toUpperCase() + mealCardType.slice(1)})`
        : paymentMethod === "kredi-karti" 
        ? "Kredi Kartı" 
        : "Nakit";

      // Clean cart items for backend (remove added_ingredients_details)
      const cleanedItems = cart.map(item => {
        const { added_ingredients_details, ...cleanItem } = item;
        return cleanItem;
      });

      const orderData = {
        order_source: "order",
        customer_id: null,
        customer_name: "Misafir",
        customer_phone: null,
        customer_address: "",
        items: cleanedItems, // Send cleaned items
        total: parseFloat(calculateTotal()),
        order_type: orderType,
        payment_status: "ödenmedi",
        payment_method: finalPaymentMethod
      };

      const response = await axios.post(`${API}/orders`, orderData);
      
      // Yazdırma için cart'taki detayları koru
      const orderWithDetails = {
        ...response.data,
        items: cart.map((cartItem, idx) => ({
          ...response.data.items[idx],
          added_ingredients_details: cartItem.added_ingredients_details
        }))
      };
      
      setLastCreatedOrder(orderWithDetails);
      setShowPaymentModal(false);
      setShowPrintModal(true);
      
      setCart([]);
      loadActiveOrders();
    } catch (error) {
      console.error("Sipariş oluşturma hatası:", error);
      console.error("Error details:", error.response?.data);
      alert("Sipariş oluşturulurken hata oluştu: " + (error.response?.data?.detail || error.message));
    }
  };

  const closePrintModal = () => {
    setShowPrintModal(false);
    setLastCreatedOrder(null);
  };

  const openFinalPayment = (order) => {
    setSelectedOrderForPayment(order);
    setPaymentMethod("nakit");
    setShowFinalPaymentModal(true);
  };

  const completeFinalPayment = async () => {
    try {
      const finalPaymentMethod = paymentMethod === "yemek-karti" 
        ? `Yemek Kartı (${mealCardType.charAt(0).toUpperCase() + mealCardType.slice(1)})`
        : paymentMethod === "kredi-karti" 
        ? "Kredi Kartı" 
        : "Nakit";

      await axios.put(`${API}/orders/${selectedOrderForPayment.id}`, {
        payment_status: "ödendi",
        payment_method: finalPaymentMethod
      });
      
      setShowFinalPaymentModal(false);
      setSelectedOrderForPayment(null);
      loadActiveOrders();
    } catch (error) {
      console.error("Ödeme hatası:", error);
      alert("Ödeme işlenirken hata oluştu");
    }
  };

  const reprintOrder = (order) => {
    setLastCreatedOrder(order);
    setShowPrintModal(true);
  };

  const handlePrintReceipt = () => {
    const printContent = document.getElementById('order-print-content');
    if (!printContent) {
      alert("Yazdırma içeriği bulunamadı");
      return;
    }

    const printWindow = window.open('', '_blank');
    printWindow.document.write('<html><head><title>Sipariş Fişi</title>');
    printWindow.document.write('<style>');
    printWindow.document.write('body { font-family: monospace; margin: 0; padding: 0; }');
    printWindow.document.write('* { box-sizing: border-box; }');
    printWindow.document.write('</style>');
    printWindow.document.write('</head><body>');
    printWindow.document.write(printContent.innerHTML);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
    
    setShowPrintModal(false);
  };

  const cancelOrder = async (orderId) => {
    if (!window.confirm("Siparişi iptal etmek istediğinize emin misiniz?")) {
      return;
    }

    try {
      await axios.put(`${API}/orders/${orderId}`, {
        payment_status: "iptal"
      });
      loadActiveOrders();
    } catch (error) {
      console.error("Sipariş iptal hatası:", error);
      alert("Sipariş iptal edilirken hata oluştu");
    }
  };

  const openEditOrder = (order) => {
    // Sadece bekleyen/ödenmedi siparişler
    if (order.payment_status !== "ödenmedi" || order.order_status !== "bekliyor") {
      alert("Sadece bekleyen sipariş düzenlenebilir");
      return;
    }

    setIsEditingOrder(true);
    setEditingOrder(order);

    // Sepete sipariş kalemlerini yükle (ürün ekleme/çıkarma aynı akıştan)
    const items = Array.isArray(order.items) ? order.items : [];
    setCart(items.map(it => ({
      product_id: it.product_id,
      product_name: it.product_name,
      quantity: it.quantity,
      price: it.price,
      size: it.size || null,
      removed_ingredients: it.removed_ingredients || [],
      added_ingredients: it.added_ingredients || [],
      special_note: it.special_note || "",
      added_ingredients_details: it.added_ingredients_details || []
    })));

    // Modal state reset
    setShowPaymentModal(false);
  };

  const saveEditedOrder = async () => {
    if (!editingOrder) return;

    try {
      // Clean cart items for backend (remove added_ingredients_details)
      const cleanedItems = cart.map(item => {
        const { added_ingredients_details, ...cleanItem } = item;
        return cleanItem;
      });

      const response = await axios.put(`${API}/orders/${editingOrder.id}/pending`, {
        items: cleanedItems,
      });

      // Yazdırma için cart'taki detayları koru (yeni total server-side)
      const orderWithDetails = {
        ...response.data,
        items: cart.map((cartItem, idx) => ({
          ...response.data.items[idx],
          added_ingredients_details: cartItem.added_ingredients_details,
        })),
      };

      setLastCreatedOrder(orderWithDetails);
      setShowPrintModal(true);

      // Edit modundan çık
      setIsEditingOrder(false);
      setEditingOrder(null);
      setCart([]);

      loadActiveOrders();
    } catch (error) {
      console.error("Sipariş düzenleme hatası:", error);
      alert("Sipariş düzenlenirken hata oluştu: " + (error.response?.data?.detail || error.message));
    }
  };

  const cancelEditOrder = () => {
    if (!window.confirm("Düzenlemeyi iptal etmek istiyor musunuz?")) return;
    setIsEditingOrder(false);
    setEditingOrder(null);
    setCart([]);
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sol: Ürünler */}
      <div className="flex-1 p-6 overflow-auto">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">
          📦 {isEditingOrder ? "Sipariş Düzenleme" : "Hızlı Sipariş"}
        </h1>
        {isEditingOrder && (
          <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start justify-between gap-4">
              <p className="text-sm text-yellow-800">
                Toplam (güncel sepet): <span className="font-bold">{calculateTotal()} ₺</span>
              </p>
              <div className="flex gap-2">
                <button
                  onClick={cancelEditOrder}
                  className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium"
                >
                  İptal
                </button>
                <button
                  onClick={saveEditedOrder}
                  className="px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 text-white font-bold"
                >
                  Kaydet & Yazdır
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Kategoriler */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-3 py-2 rounded-lg font-medium text-sm transition ${
                  selectedCategory === cat.id
                    ? "bg-orange-600 text-white shadow-md"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* Ürün Listesi */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredProducts.map(product => (
            <div
              key={product.id}
              onClick={() => addToCart(product)}
              className="bg-white rounded-lg shadow-md p-4 cursor-pointer hover:shadow-xl transition hover:scale-105"
            >
              <h3 className="font-bold text-gray-800 mb-2">{product.name}</h3>
              <p className="text-orange-600 font-bold text-lg">{product.price} ₺</p>
            </div>
          ))}
        </div>
      </div>

      {/* Sağ: Sepet ve Aktif Siparişler */}
      <div className="w-[500px] bg-white shadow-xl p-6 overflow-auto">
        {/* Sepet - Daha büyük alan */}
        <div className="mb-6" style={{ minHeight: '55vh' }}>
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <ShoppingCart size={24} className="mr-2" />
            Sepet ({cart.length})
          </h2>

          {cart.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Sepet boş</p>
          ) : (
            <>
              <div className="space-y-3 mb-4">
                {cart.map((item, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <h4 className="font-semibold text-sm">{item.product_name}</h4>
                        {item.size && (
                          <div className="text-xs text-purple-600 font-medium mt-1">
                            🍕 {item.size}
                          </div>
                        )}
                        {item.removed_ingredients && item.removed_ingredients.length > 0 && (
                          <div className="text-xs text-red-600 mt-1">
                            ❌ {item.removed_ingredients.join(", ")}
                          </div>
                        )}
                        {item.added_ingredients && item.added_ingredients.length > 0 && (
                          <div className="text-xs text-green-600 mt-1">
                            ➕ {item.added_ingredients.join(", ")}
                          </div>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            const product = products.find(p => p.id === item.product_id);
                            if (product) openIngredientModal(product, index);
                          }}
                          className="text-blue-500 hover:text-blue-700"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => removeFromCart(item.product_id, index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => updateQuantity(index, -1)}
                          className="w-7 h-7 bg-gray-200 rounded flex items-center justify-center hover:bg-gray-300"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="w-8 text-center font-bold">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(index, 1)}
                          className="w-7 h-7 bg-gray-200 rounded flex items-center justify-center hover:bg-gray-300"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <span className="font-bold text-orange-600">
                        {(item.price * item.quantity).toFixed(2)} ₺
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Toplam ve Sipariş Butonları */}
              <div className="border-t pt-4 mb-4">
                <div className="flex justify-between items-center text-xl font-bold mb-4">
                  <span>Toplam:</span>
                  <span className="text-orange-600">{calculateTotal()} ₺</span>
                </div>

                {!isEditingOrder && (
                  <div className="space-y-2">
                    <button
                      onClick={() => createOrder("paket")}
                      className="w-full py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-bold"
                    >
                      📦 Paket Sipariş Ver
                    </button>
                    <button
                      onClick={() => createOrder("gel-al")}
                      className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-bold"
                    >
                      🏃 Gel-Al Sipariş Ver
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Aktif Siparişler - Daha küçük alan */}
        <div className="border-t pt-6" style={{ maxHeight: '35vh', overflowY: 'auto' }}>
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            Aktif Siparişler ({activeOrders.length})
          </h2>

          {activeOrders.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Aktif sipariş yok</p>
          ) : (
            <div className="space-y-3">
              {activeOrders.map(order => (
                <div key={order.id} className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-bold text-gray-800">
                          {order.order_type === "paket" ? "📦 Paket" : "🏃 Gel-Al"}
                        </h4>
                        {order.payment_status === "ödenmedi" && order.order_status === "bekliyor" && (
                          <button
                            onClick={() => openEditOrder(order)}
                            className="p-1.5 rounded-md hover:bg-orange-100 text-orange-600"
                            title="Siparişi Düzenle"
                          >
                            <Edit2 size={16} />
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(order.created_at).toLocaleString("tr-TR")}
                      </p>
                      <p className="text-xs font-bold text-red-600 mt-1">
                        ⏱️ Geçen: {getElapsedTime(order.created_at)}
                      </p>
                    </div>
                    <span className="font-bold text-orange-600">{order.total} ₺</span>
                  </div>
                  <div className="text-sm text-gray-700 mb-3">
                    {order.items.map((item, idx) => (
                      <div key={idx}>• {item.quantity}x {item.product_name}</div>
                    ))}
                  </div>
                  <div className="space-y-2">
                    <button
                      onClick={() => openFinalPayment(order)}
                      className="w-full py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                    >
                      ✓ Öde ve Kapat
                    </button>
                    <button
                      onClick={() => reprintOrder(order)}
                      className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                    >
                      🖨️ Siparişi Yazdır
                    </button>


                    <button
                      onClick={() => cancelOrder(order.id)}
                      className="w-full py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
                    >
                      ✕ İptal
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Ödeme Modalı */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-[500px] max-h-[90vh] flex flex-col">
            {/* Header - Sabit */}
            <div className="p-6 border-b">
              <h2 className="text-2xl font-bold">Ödeme Yöntemi</h2>
            </div>

            {/* İçerik - Kaydırmalı */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="bg-blue-50 p-4 rounded-lg mb-6 text-center">
                <p className="text-4xl font-bold text-blue-700">
                  {calculateTotal()} ₺
                </p>
              </div>

              <div className="space-y-3 mb-6">
                <button
                  onClick={() => setPaymentMethod("nakit")}
                  className={`w-full p-4 rounded-lg border-2 ${
                    paymentMethod === "nakit"
                      ? "border-green-500 bg-green-50"
                      : "border-gray-200"
                  }`}
                >
                  💵 Nakit
                </button>

                <button
                  onClick={() => setPaymentMethod("kredi-karti")}
                  className={`w-full p-4 rounded-lg border-2 ${
                    paymentMethod === "kredi-karti"
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200"
                  }`}
                >
                  💳 Kredi Kartı
                </button>

                <button
                  onClick={() => setPaymentMethod("yemek-karti")}
                  className={`w-full p-4 rounded-lg border-2 ${
                    paymentMethod === "yemek-karti"
                      ? "border-purple-500 bg-purple-50"
                      : "border-gray-200"
                  }`}
                >
                  🎫 Yemek Kartı
                </button>

                {paymentMethod === "yemek-karti" && (
                  <div className="ml-8 space-y-2">
                    {["sodexo", "ticket", "multinet", "metropol"].map(type => (
                      <button
                        key={type}
                        onClick={() => setMealCardType(type)}
                        className={`w-full p-3 rounded-lg border ${
                          mealCardType === type
                            ? "border-purple-500 bg-purple-100"
                            : "border-gray-200"
                        }`}
                      >
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Butonlar - Sabit */}
            <div className="p-6 border-t flex space-x-3">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="flex-1 py-3 bg-gray-300 rounded-lg"
              >
                İptal
              </button>
              <button
                onClick={completeOrder}
                className="flex-1 py-3 bg-orange-600 text-white rounded-lg font-bold"
              >
                Sipariş Ver
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Yazdırma Modalı */}
      {showPrintModal && lastCreatedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-[400px] p-6">
            <h2 className="text-2xl font-bold text-center mb-4">✅ Sipariş Alındı!</h2>
            
            <div className="border-2 border-dashed border-gray-300 p-4 mb-4">
              <p className="text-center text-sm text-gray-600 mb-2">Sipariş #{lastCreatedOrder.id}</p>
              <p className="text-center text-3xl font-bold text-orange-600 mb-2">
                {lastCreatedOrder.total} ₺
              </p>
              <p className="text-center text-sm text-gray-600">
                {lastCreatedOrder.order_type === "paket" ? "📦 Paket" : "🏃 Gel-Al"}
              </p>
              <p className="text-center text-sm text-gray-600 mt-2">
                Ödeme: {lastCreatedOrder.payment_method}
              </p>
            </div>

            {/* Gizli yazdırma içeriği */}
            <div id="order-print-content" style={{ display: 'none' }}>
              <div style={{ 
                fontFamily: 'monospace', 
                width: '300px', 
                margin: '0 auto', 
                padding: '15px',
                fontSize: '12px', // 11px → 12px
                lineHeight: '1.4'
              }}>
                {/* Header */}
                <div style={{ textAlign: 'center', borderBottom: '2px double black', paddingBottom: '8px', marginBottom: '10px' }}>
                  <h2 style={{ fontSize: '19px', fontWeight: 'bold', margin: '0 0 4px 0' }}>🍕 VERONA PIZZA</h2>
                  <p style={{ fontSize: '13px', fontWeight: 'bold', margin: '3px 0' }}>HIZLI SİPARİŞ</p>
                  <p style={{ fontSize: '11px', margin: '2px 0' }}>{new Date().toLocaleString("tr-TR")}</p>
                </div>

                {/* Order Info */}
                <div style={{ marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid black' }}>
                  <p style={{ margin: '3px 0', fontSize: '12px' }}>
                    <b>TİP:</b> {lastCreatedOrder.order_type === "paket" ? "📦 Paket" : "🏃 Gel-Al"}
                  </p>
                </div>

                {/* Items */}
                <div style={{ marginBottom: '10px' }}>
                  <h3 style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '8px', textAlign: 'center', borderBottom: '2px solid black', paddingBottom: '4px' }}>
                    SİPARİŞ DETAYI
                  </h3>
                  
                  {lastCreatedOrder.items.map((item, idx) => (
                    <div key={idx} style={{ marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px dashed black' }}>
                      {/* Product Name with Adet and Size */}
                      <p style={{ fontSize: '12px', margin: '2px 0', fontWeight: 'bold' }}>
                        {item.quantity} Adet - {item.product_name}
                        {item.size && <span style={{ fontWeight: 'normal' }}> ({item.size})</span>}
                      </p>

                      {/* Removed Ingredients */}
                      {item.removed_ingredients && item.removed_ingredients.length > 0 && (
                        <p style={{ fontSize: '11px', margin: '4px 0 2px 10px', lineHeight: '1.3' }}>
                          <b>❌ ÇIKARILACAK:</b> {item.removed_ingredients.join(", ")}
                        </p>
                      )}

                      {/* Added Ingredients */}
                      {item.added_ingredients_details && item.added_ingredients_details.length > 0 && (
                        <p style={{ fontSize: '11px', margin: '4px 0 2px 10px', lineHeight: '1.3' }}>
                          <b>➕ EKSTRA:</b><br />
                          {item.added_ingredients_details.map((extra, i) => (
                            <span key={i}>
                              &nbsp;&nbsp;• {extra.name} (+{extra.price.toFixed(2)}₺){i < item.added_ingredients_details.length - 1 ? <br /> : ''}
                            </span>
                          ))}
                        </p>
                      )}
                      {!item.added_ingredients_details && item.added_ingredients && item.added_ingredients.length > 0 && (
                        <p style={{ fontSize: '11px', margin: '4px 0 2px 10px', lineHeight: '1.3' }}>
                          <b>➕ EKSTRA:</b> {item.added_ingredients.join(", ")}
                        </p>
                      )}

                      {/* Special Note */}
                      {item.special_note && (
                        <p style={{ fontSize: '11px', margin: '4px 0 2px 10px', fontStyle: 'italic', lineHeight: '1.3' }}>
                          <b>📝 NOT:</b> {item.special_note}
                        </p>
                      )}

                      {/* Price */}
                      <p style={{ textAlign: 'right', fontSize: '13px', fontWeight: 'bold', marginTop: '4px' }}>
                        {(item.price * item.quantity).toFixed(2)} ₺
                      </p>
                    </div>
                  ))}
                </div>

                {/* Total */}
                <div style={{ borderTop: '3px double black', paddingTop: '8px', marginBottom: '10px' }}>
                  <p style={{ fontSize: '15px', fontWeight: 'bold', margin: '5px 0', textAlign: 'center' }}>
                    TOPLAM: {lastCreatedOrder.total.toFixed(2)} ₺
                  </p>
                  <p style={{ fontSize: '12px', marginTop: '5px', textAlign: 'center' }}>
                    <b>ÖDEME:</b> {lastCreatedOrder.payment_method}
                  </p>
                </div>

                {/* Footer */}
                <div style={{ textAlign: 'center', paddingTop: '8px', borderTop: '1px dashed black', fontSize: '11px' }}>
                  <p style={{ fontSize: '13px', fontWeight: 'bold', margin: '5px 0' }}>
                    🍕 AFİYET OLSUN! 🍕
                  </p>
                  <p style={{ fontSize: '10px', margin: '3px 0' }}>
                    Verona Pizza
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <button
                onClick={handlePrintReceipt}
                className="w-full py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700"
              >
                🖨️ Fiş Yazdır
              </button>
              <button
                onClick={closePrintModal}
                className="w-full py-3 bg-gray-300 rounded-lg hover:bg-gray-400"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gerçek Ödeme Modalı */}
      {showFinalPaymentModal && selectedOrderForPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg w-[500px] max-h-[90vh] flex flex-col">
            {/* Header - Sabit */}
            <div className="p-6 border-b">
              <h2 className="text-2xl font-bold text-green-700">💰 Ödeme Alın</h2>
            </div>

            {/* İçerik - Kaydırmalı */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="bg-green-50 p-4 rounded-lg mb-6 text-center">
                <p className="text-4xl font-bold text-green-700">
                  {selectedOrderForPayment.total.toFixed(2)} ₺
                </p>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => setPaymentMethod("nakit")}
                  className={`w-full p-4 rounded-lg border-2 transition ${
                    paymentMethod === "nakit"
                      ? "border-green-500 bg-green-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  💵 Nakit
                </button>

                <button
                  onClick={() => setPaymentMethod("kredi-karti")}
                  className={`w-full p-4 rounded-lg border-2 transition ${
                    paymentMethod === "kredi-karti"
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  💳 Kredi Kartı
                </button>

                <button
                  onClick={() => setPaymentMethod("yemek-karti")}
                  className={`w-full p-4 rounded-lg border-2 transition ${
                    paymentMethod === "yemek-karti"
                      ? "border-purple-500 bg-purple-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  🎫 Yemek Kartı
                </button>

                {paymentMethod === "yemek-karti" && (
                  <div className="ml-8 space-y-2">
                    {["sodexo", "ticket", "multinet", "metropol"].map(type => (
                      <button
                        key={type}
                        onClick={() => setMealCardType(type)}
                        className={`w-full p-3 rounded-lg border transition ${
                          mealCardType === type
                            ? "border-purple-500 bg-purple-100"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Butonlar - Sabit */}
            <div className="p-6 border-t bg-white flex space-x-3">
              <button
                onClick={() => setShowFinalPaymentModal(false)}
                className="flex-1 py-3 bg-gray-300 rounded-lg hover:bg-gray-400 transition"
              >
                İptal
              </button>
              <button
                onClick={completeFinalPayment}
                className="flex-1 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition"
              >
                ✓ Ödemeyi Tamamla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Malzeme Modalı (basitleştirilmiş) - Yer kazanmak için kısaltıldı */}
      {showIngredientModal && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg w-[600px] max-h-[90vh] overflow-auto">
            <div className="p-6 border-b">
              <h2 className="text-2xl font-bold">
                🍕 {selectedProduct.name}
              </h2>
            </div>

            <div className="p-6">
              {selectedProduct.sizes && selectedProduct.sizes.length > 0 && (
                <div className="mb-4">
                  <h3 className="font-bold mb-2">Boyut</h3>
                  <div className="grid grid-cols-3 gap-2">
                    {selectedProduct.sizes.map((size) => (
                      <button
                        key={size.name}
                        onClick={() => setSelectedSize(size.name)}
                        className={`p-3 rounded-lg border-2 ${
                          selectedSize === size.name
                            ? "border-orange-500 bg-orange-50"
                            : "border-gray-200"
                        }`}
                      >
                        <div className="font-semibold">{size.name}</div>
                        <div className="text-sm">{size.price} ₺</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {selectedProduct.ingredients && selectedProduct.ingredients.length > 0 && (
                <div className="mb-4 border-2 border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setIsIngredientsOpen(!isIngredientsOpen)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition"
                  >
                    <div className="flex items-center space-x-2">
                      <h3 className="font-bold">Malzeme Çıkar</h3>
                      {removedIngredients.length > 0 && (
                        <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                          {removedIngredients.length} çıkarıldı
                        </span>
                      )}
                    </div>
                    {isIngredientsOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                  
                  {isIngredientsOpen && (
                    <div className="p-4 bg-white">
                      <div className="grid grid-cols-2 gap-2">
                        {selectedProduct.ingredients.map((ingredient) => (
                          <button
                            key={ingredient}
                            onClick={() => toggleRemovedIngredient(ingredient)}
                            className={`p-2 rounded-lg border-2 text-sm transition ${
                              removedIngredients.includes(ingredient)
                                ? "border-red-500 bg-red-50"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                          >
                            {removedIngredients.includes(ingredient) && "❌ "}
                            {ingredient}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {selectedProduct.extra_ids && selectedProduct.extra_ids.length > 0 && (
                <div className="mb-4 border-2 border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setIsExtrasOpen(!isExtrasOpen)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition"
                  >
                    <div className="flex items-center space-x-2">
                      <h3 className="font-bold">Ekstra Ekle</h3>
                      {addedIngredients.length > 0 && (
                        <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                          {addedIngredients.length} seçili
                        </span>
                      )}
                    </div>
                    {isExtrasOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                  
                  {isExtrasOpen && (
                    <div className="p-4 bg-white">
                      <div className="grid grid-cols-2 gap-2">
                        {extras
                          .filter((extra) => selectedProduct.extra_ids.includes(extra.id))
                          .map((extra) => (
                            <button
                              key={extra.id}
                              onClick={() => toggleAddedIngredient(extra.id)}
                              className={`p-2 rounded-lg border-2 text-sm ${
                                addedIngredients.includes(extra.id)
                                  ? "border-green-500 bg-green-50"
                                  : "border-gray-200"
                              }`}
                            >
                              {addedIngredients.includes(extra.id) && "✓ "}
                              {extra.name}
                            </button>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Ürün Notu */}
              <div className="mb-6">
                <h3 className="font-bold mb-2 text-gray-700">Ürün Notu (Opsiyonel)</h3>
                <textarea
                  value={specialNote}
                  onChange={(e) => setSpecialNote(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={2}
                  placeholder="Örn: Az pişmiş olsun, soğansız..."
                />
              </div>


              <div className="mb-4">
                <h3 className="font-bold mb-2">Adet</h3>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setCustomQuantity(Math.max(1, customQuantity - 1))}
                    className="w-10 h-10 bg-gray-200 rounded-lg"
                  >
                    <Minus size={20} className="mx-auto" />
                  </button>
                  <span className="text-2xl font-bold w-12 text-center">{customQuantity}</span>
                  <button
                    onClick={() => setCustomQuantity(customQuantity + 1)}
                    className="w-10 h-10 bg-gray-200 rounded-lg"
                  >
                    <Plus size={20} className="mx-auto" />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6 border-t flex space-x-3">
              <button
                onClick={() => setShowIngredientModal(false)}
                className="flex-1 py-3 bg-gray-300 rounded-lg"
              >
                İptal
              </button>
              <button
                onClick={addCustomizedToCart}
                className="flex-1 py-3 bg-orange-600 text-white rounded-lg font-bold"
              >
                Sepete Ekle
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderPage;
